/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography, useTheme } from '@mui/material';

import {
  FinderFiltersResponse,
  FinderResultsRequest,
  TimeDuration,
  getFinderCinemas,
  getFinderDates,
  getFinderExperiences,
  getFinderFilms,
  getFinderResultCount,
} from '@/logic/services/movie-finder';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate, useQuery } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosError } from 'axios';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCity } from '@/logic/hooks';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useMovieFinder } from '@/logic/state';
import { EventTypes, MovieFinderModel } from '@/logic/types/data-layer-models';
import { useRouter } from 'next/router';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import { Search, Searchable } from '@/components/movie-finder/search';
import { MovieFinderResults } from '@/components/movie-finder/results';
import { Filters } from '@/components/movie-finder/filters';
import { FinderTabs } from '@/components/movie-finder/tabs';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const MovieFinder: NextPage<{
  path: string;
}> = ({ path }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { cityId, getCity } = useCity();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [finderFiltersResults, setFinderFiltersResults] =
    useState<FinderFiltersResponse>(null);
  const { data: finderDates } = useQuery(['movie-finder-dates'], () => getFinderDates(), {
    enabled: false,
  });

  const [
    selectedDate,
    selectedMovies,
    selectedTimeDuration,
    selectedCinemas,
    selectedExperiences,
    isShowResults,
    searchText,
    setSelectedDate,
    setSelectedTimeDuration,
    setSelectedMovies,
    setSelectedCinemas,
    setSelectedExperiences,
    setIsShowResults,
  ] = useMovieFinder(
    s => [
      s.selectedDate,
      s.selectedMovies,
      s.selectedTimeDuration,
      s.selectedCinemas,
      s.selectedExperiences,
      s.isShowResults,
      s.searchText,
      s.setSelectedDate,
      s.setSelectedTimeDuration,
      s.setSelectedMovies,
      s.setSelectedCinemas,
      s.setSelectedExperiences,
      s.setIsShowResults,
    ],
    shallow
  );

  const params: FinderResultsRequest = useMemo(
    () => ({
      cityId,
      date: selectedDate,
      timeDuration: selectedTimeDuration,
      filmIds: selectedMovies,
      cinemaIds: selectedCinemas,
      experienceIds: selectedExperiences,
    }),
    [
      cityId,
      selectedDate,
      selectedTimeDuration,
      selectedMovies,
      selectedCinemas,
      selectedExperiences,
    ]
  );

  const {
    data: filterFilmsResponse,
    refetch: refetchFilms,
    isFetching: isFetchingFilms,
    isError: isErrorFilmsResponse,
  } = useQuery(['movie-finder-films', params], () => getFinderFilms(params), {
    enabled: !!selectedDate && !!cityId && !!selectedDate,
  });

  const {
    data: filterCinemasResponse,
    refetch: refetchCinemas,
    isFetching: isFetchingCinemas,
    isError: isErrorCinemasResponse,
  } = useQuery(['movie-finder-cinemas', params], () => getFinderCinemas(params), {
    enabled: !!selectedDate && !!cityId && !!selectedDate,
  });

  const {
    data: filterExperiencesResponse,
    refetch: refetchExperiences,
    isFetching: isFetchingExperiences,
    isError: isErrorExperiencesResponse,
  } = useQuery(['movie-finder-experiences', params], () => getFinderExperiences(params), {
    enabled: !!selectedDate && !!cityId && !!selectedDate,
  });

  const {
    data: filterResultsCountResponse,
    refetch: refetchResultsCount,
    isFetching: isFetchingResultsCount,
  } = useQuery(
    ['movie-finder-results-count', params],
    () => getFinderResultCount(params),
    {
      enabled: !!selectedDate && !!cityId && !!selectedDate,
    }
  );

  const tryAgain = (): void => {
    refetchFilms();
    refetchCinemas();
    refetchExperiences();
    refetchResultsCount();
  };

  useEffect(() => {
    if (
      isFetchingFilms ||
      isFetchingCinemas ||
      isFetchingExperiences ||
      isFetchingResultsCount
    ) {
      setIsFetching(true);
    } else {
      setIsFetching(false);
    }
  }, [isFetchingFilms, isFetchingCinemas, isFetchingExperiences, isFetchingResultsCount]);

  useEffect(() => {
    setFinderFiltersResults(prevValues => ({
      ...prevValues,
      films: filterFilmsResponse?.films,
      cinemas: filterCinemasResponse?.cinemas,
      experiences: filterExperiencesResponse?.experiences,
      totalResultCount: filterResultsCountResponse?.totalResultCount,
    }));
  }, [
    filterFilmsResponse,
    filterCinemasResponse,
    filterExperiencesResponse,
    filterResultsCountResponse,
  ]);

  useEffect(() => {
    const { filmIds, experienceIds, cinemaIds } = params;
    if (filmIds.length === 0 && experienceIds.length === 0 && cinemaIds.length === 0)
      setIsShowResults(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    setSelectedDate(finderDates.avaliableDates[0].date);
    setSelectedTimeDuration(TimeDuration.ALL_DAY);
    setSelectedCinemas([]);
    setSelectedExperiences([]);
    setSelectedMovies([]);
    handleHideResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId]);

  const handleShowResults = (): void => {
    const data: MovieFinderModel = {
      event: EventTypes.MOVIE_FINDER,
      language: router.locale.toUpperCase() as 'AR' | 'EN',
      location: getCity(cityId)?.name,
      page_title: document.title,
      page_url: document.URL,
      search_text: searchText,
    };
    triggerGAEvent(data);
    setIsShowResults(true);
  };

  const handleHideResults = useCallback((): void => {
    setIsShowResults(false);
  }, []);

  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.finder);

  const [itemToScroll, setItemToScroll] = useState<{ type: Searchable; id: number }>(
    null
  );

  const handleScrollToItem = (type: Searchable, id: number): void => {
    setItemToScroll({ type, id });
  };

  return (
    <>
      <Seo
        title={seoPage?.titleTag || intl.formatMessage({ id: 'movie.finder' })}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      {isShowResults && (
        <Box>
          <MovieFinderResults
            filters={params}
            finderFiltersResults={finderFiltersResults}
            handleEditFilters={handleHideResults}
          />
        </Box>
      )}
      {!isShowResults && (
        <Box
          sx={{
            marginBlock: {
              mobile: '32px 60px',
              tablet: pxToRem(52),
              position: 'relative',
            },
            cursor: isFetching ? 'progress' : 'unset',
          }}
        >
          <Box
            sx={{
              marginInline: {
                mobile: '16px',
                tablet: pxToRem(128),
              },
            }}
          >
            <Typography
              component='h1'
              sx={{
                color: theme.palette.grey[80],
                fontSize: pxToRem(30),
                fontWeight: 700,
                lineHeight: pxToRem(24),
                marginBottom: {
                  mobile: '24px',
                  tablet: pxToRem(52),
                },
              }}
            >
              <FormattedMessage id='movie.finder' />
            </Typography>
            <Search scrollToItem={handleScrollToItem} />
            <Filters finderFiltersResults={finderFiltersResults} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginTop: {
                  mobile: '24px',
                  tablet: pxToRem(56),
                },
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <Typography
                  sx={{
                    fontSize: {
                      mobile: '18px',
                      tablet: pxToRem(18),
                    },
                    fontFamily: theme.palette.typography.font.bold,
                    color: theme.palette.grey[90],
                    fontWeight: 700,
                    lineHeight: '27px',
                  }}
                >
                  <FormattedMessage id='movie.finder.select_your' />
                </Typography>
                <Typography
                  sx={{
                    fontSize: {
                      mobile: '18px',
                      tablet: pxToRem(18),
                    },
                    fontFamily: theme.palette.typography.font.bold,
                    color: theme.palette.grey[90],
                    fontWeight: 700,
                    lineHeight: '27px',
                    textTransform: 'lowercase',
                    marginInlineStart: '4px',
                  }}
                >
                  {activeTab && <FormattedMessage id={activeTab} />}
                </Typography>
              </Box>
              <Button
                id='show-results-button'
                loading={isFetching}
                component='a'
                variant='contained'
                onClick={handleShowResults}
                disabled={!finderFiltersResults?.totalResultCount}
                sx={{
                  position: {
                    mobile: 'absolute',
                    tablet: 'relative',
                  },
                  right: 0,
                  top: {
                    mobile: 'calc(100% + 10px)',
                    tablet: 0,
                  },
                  width: {
                    mobile: 'calc(100% - 45px)',
                    tablet: 'auto',
                  },
                  margin: {
                    mobile: '0 30px',
                    tablet: 0,
                  },
                  zIndex: {
                    mobile: 100,
                    tablet: 'unset',
                  },
                  height: { tablet: pxToRem(56), mobile: '56px' },
                }}
              >
                <FormattedMessage
                  id='movie_finder.show_results'
                  values={{
                    count: finderFiltersResults?.totalResultCount ?? 0,
                  }}
                />
              </Button>
            </Box>
          </Box>
          <FinderTabs
            finderDates={finderDates}
            finderFiltersResults={finderFiltersResults}
            isFiltersLoading={isFetching}
            itemToScroll={itemToScroll}
            isError={
              isErrorFilmsResponse || isErrorCinemasResponse || isErrorExperiencesResponse
            }
            setActiveTab={setActiveTab}
            tryAgain={tryAgain}
          />
        </Box>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const reqConfig = {
    headers: {
      ...getLangHeaders(context.locale),
    },
  };
  const { locale, defaultLocale } = context;
  const headers = {
    ...getLangHeaders(context.locale),
  };

  const queryClient = new QueryClient();
  let finderDatesProp;
  try {
    finderDatesProp = await queryClient.fetchQuery(['movie-finder-dates'], () =>
      getFinderDates(reqConfig)
    );
  } catch (error) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  const params: FinderResultsRequest = {
    date: finderDatesProp?.avaliableDates[0]?.date,
    timeDuration: TimeDuration.ALL_DAY,
    cityId: Number(context.req.cookies.city),
    filmIds: [],
    cinemaIds: [],
    experienceIds: [],
  };
  const { cityId, date, timeDuration, filmIds, cinemaIds, experienceIds } = params;

  if (!!cityId && !!date && !!timeDuration) {
    try {
      await queryClient.fetchQuery(
        [
          'movie-finder-films',
          cityId,
          date,
          timeDuration,
          filmIds,
          cinemaIds,
          experienceIds,
        ],
        () => getFinderFilms(params, reqConfig)
      );
      await queryClient.fetchQuery(
        [
          'movie-finder-cinemas',
          cityId,
          date,
          timeDuration,
          filmIds,
          cinemaIds,
          experienceIds,
        ],
        () => getFinderCinemas(params, reqConfig)
      );
      await queryClient.fetchQuery(
        [
          'movie-finder-experiences',
          cityId,
          date,
          timeDuration,
          filmIds,
          cinemaIds,
          experienceIds,
        ],
        () => getFinderExperiences(params, reqConfig)
      );

      await queryClient.fetchQuery(
        [
          'movie-finder-results-count',
          cityId,
          date,
          timeDuration,
          filmIds,
          cinemaIds,
          experienceIds,
        ],
        () => getFinderResultCount(params, reqConfig)
      );
    } catch (error: unknown) {
      if (is500(error)) {
        return handleError(error as AxiosError);
      }
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default MovieFinder;
